import React from 'react';
import {
  Button,
  Typography,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import styled from '@emotion/styled';
import FilterIcon from '../../../assets/icons/filter.svg';
import { useTranslation } from 'react-i18next';

interface FilterPopoverProps {
  label: string;
  options: string[];
  selectedOptions: string[];
  onToggle: (option: string) => void;
  anchorEl: HTMLElement | null;
  open: boolean;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  optionLabelMapper?: (option: string) => string;
}

const FilterButton = styled(Button)`
  && {
    min-width: 30px;
    color: var(--color-light-greyish-blue);
    padding: 0;
    min-height: unset;
    &:hover {
      background-color: transparent;
    }
    &:focus-visible {
      outline: 2px solid var(--color-white);
      outline-offset: 2px;
    }
  }
`;

const FilterLabel = styled(Typography)<{ component: string }>`
  && {
    color: var(--color-light-greyish-blue);
    font-size: 14px;
  }
`;

export const FilterPopover: React.FC<FilterPopoverProps> = ({
  label,
  options,
  selectedOptions,
  onToggle,
  anchorEl,
  open,
  onOpen,
  onClose,
  optionLabelMapper,
}) => {
  const { t } = useTranslation();

  if (options.length === 0) {
    return (
      <FilterLabel component="p" variant="subtitle1">
        {label}
      </FilterLabel>
    );
  }

  const handleToggle = (option: string) => {
    onToggle(option);
    onClose();
  };

  const filterContent = (
    <FormGroup
      sx={{
        p: 14,
        border: '1px solid var(--color-dark-greyish-blue)',
        borderRadius: 1,
        backgroundColor: 'var(--color-white)',
      }}
    >
      <div className="mb-2">
        <span>
          {t('agreements.internalSearch.filter_dropdown_accessibility_info')}
        </span>
      </div>
      {options.map((option) => (
        <FormControlLabel
          key={option}
          control={
            <Checkbox
              checked={selectedOptions.includes(option)}
              onChange={() => handleToggle(option)}
            />
          }
          label={optionLabelMapper ? optionLabelMapper(option) : option}
        />
      ))}
    </FormGroup>
  );

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FilterLabel component="p" variant="subtitle1">
          {label}
        </FilterLabel>
        <FilterButton
          aria-label={`${label} filter button`}
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={onOpen}
        >
          <img src={FilterIcon} alt="" width={20} height={20} />
        </FilterButton>
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filterContent}
      </Popover>
    </>
  );
};
